import { Navigate, useLocation } from 'react-router-dom'
import { useAuth } from '@/hooks/useAuth'
import { useLicense } from '@/hooks/useLicense'
import { ReactNode, useRef, useEffect, useState } from 'react'
import { authService } from '@/services/authService'
import ContractAgreementModal, { type ContractAgreementModalRef } from '@/components/modals/ContractAgreementModal'

interface ProtectedRouteProps {
  children: ReactNode
}

export default function ProtectedRoute({ children }: ProtectedRouteProps) {
  const { isAuthenticated, user, logout, updateUser } = useAuth()
  const { expired: licenseExpired } = useLicense()
  const location = useLocation()
  const contractModalRef = useRef<ContractAgreementModalRef>(null)
  const [hasCheckedContract, setHasCheckedContract] = useState(false)

  // Check if user needs to agree to contract
  useEffect(() => {
    if (isAuthenticated && user && !hasCheckedContract) {
      // Check if contract_agreement is null or undefined (not yet agreed)
      const needsToAgree = !user.contract_agreement

      if (needsToAgree) {
        contractModalRef.current?.open()
      }

      setHasCheckedContract(true)
    }
  }, [isAuthenticated, user, hasCheckedContract])

  if (!isAuthenticated) {
    return <Navigate to="/login" replace />
  }

  // Still signed in with a password an administrator set, so two people know
  // it. Confine the session to the change-password page until that stops being
  // true. The server refuses every other route anyway; this is the matching UI
  // behaviour, and without it the console would render pages whose every
  // request comes back 403.
  //
  // Checked before the license gate on purpose: an owner whose own password was
  // just reset has to be able to change it before being sent to Plans.
  if (user?.must_change_password && location.pathname !== '/settings/password') {
    return <Navigate to="/settings/password?reason=must_change" replace />
  }

  // An administrative account with no second factor. Held to the profile page,
  // where enrolment lives, for the same reason as above: the server refuses
  // everything else anyway.
  //
  // After the password gate, never alongside it. Somebody who owes both is sent
  // to the password first, because that is the one an administrator already
  // knows and enrolling a factor onto a shared password protects the wrong
  // thing.
  if (
    !user?.must_change_password &&
    user?.must_enrol_totp &&
    location.pathname !== '/settings/profile'
  ) {
    return <Navigate to="/settings/profile?reason=totp_required" replace />
  }

  // When the license is expired, confine every logged-in user (owner/admin are
  // the only ones who can authenticate at all) to the Plans page so they can
  // only renew the license. Any other route is redirected to Plans. The backend
  // enforces the actual lockdown; this is the matching UI behaviour.
  if (licenseExpired && !location.pathname.startsWith('/plans')) {
    return <Navigate to="/plans?reason=license_expired" replace />
  }

  const handleAgreeContract = async () => {
    // Call agreeContract which will automatically fetch updated profile
    const updatedUser = await authService.agreeContract()
    // Update user state in context
    updateUser(updatedUser)
  }

  const handleDeclineContract = () => {
    logout()
  }

  return (
    <>
      {children}
      <ContractAgreementModal
        ref={contractModalRef}
        onAgree={handleAgreeContract}
        onDecline={handleDeclineContract}
      />
    </>
  )
}
